import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import env from '../../configs/env';
import { FileBrowserStorageObjectFragment } from '../../graphql/generated';
import {
  FileBrowserPageParams,
  navigateToDirectory,
} from '../../pages/file-browser-page/file-browser-page';

export const DEFAULT_PAGE_SIZE = env.app.defaults?.paginationLimit || 50;

type PaginationConfig = {
  offset?: number;
  limit?: number;
};

type UsePaginationProps = {
  /**
   * The total number of items in the current directory
   */
  fileCount: number;

  /**
   * The total number of items to show on each page
   */
  pageSize?: number;

  /**
   * The storage objects for the current directory
   */
  files: FileBrowserStorageObjectFragment[];
};

export type UsePaginationBag = {
  /**
   * The total number of items to show on each page
   */
  pageSize: number;

  /**
   * The total number of pages
   */
  pageCount: number;

  /**
   * The current page of data
   */
  page: FileBrowserStorageObjectFragment[];

  /**
   * The total number of items in the current directory
   */
  totalItemCount: number;

  /**
   * The active page number
   */
  currentPageNumber: number;

  /**
   * Move to the a different page
   */
  handlePageChange: (pageNumber: number) => void;
};

/**
 *
 * @param files
 * @param fileCount
 * @param pageSize
 */
export const usePagination = ({
  files,
  fileCount,
  pageSize = DEFAULT_PAGE_SIZE,
}: UsePaginationProps): UsePaginationBag => {
  const navigate = useNavigate();
  const params = useParams() as FileBrowserPageParams;
  const pageCount = Math.ceil(fileCount / pageSize);
  const currentPageNumber = useGetCurrentPageNumber();

  const handlePageChange = (pageNumber: number) => {
    const newPath = navigateToDirectory(params, {
      page: pageNumber.toString(),
    });

    navigate(newPath);
  };

  return {
    page: files,
    pageSize,
    pageCount,
    handlePageChange,
    totalItemCount: fileCount,
    currentPageNumber: currentPageNumber,
  };
};

export const useGetCurrentPageNumber = (): number => {
  const [searchParams] = useSearchParams();
  const currentPageNumberString = searchParams.get('page');
  const currentPageNumber = currentPageNumberString
    ? parseInt(currentPageNumberString, 10)
    : 1;

  return currentPageNumber;
};

export const usePaginationConfig = (
  pageSize: number = DEFAULT_PAGE_SIZE
): PaginationConfig => {
  const currentPageNumber = useGetCurrentPageNumber();

  return {
    offset: (currentPageNumber - 1) * pageSize,
    limit: pageSize,
  };
};
